import {Crown} from 'lucide-react';
import {PropsWithChildren} from 'react';
import {useTheme} from 'styled-components';
import {useTranslation} from 'react-i18next';
import ChekinProSettings from 'ChekinProSettings';
import {GUEST_STATUSES, ShortGuest} from '@chekin/core';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useGuestGroup, useReservationSettings} from 'hooks';
import Button from 'components/common/Button';
import {GuestItem} from 'components/GuestsPanel/components/GuestItem';
import {MainLoader, cn} from '@chekin/guestapp-ui-kit';
import {LoaderWrapper} from './styled';
import {
  Title,
  SubTitle,
  StyledIcon,
  RegisterButton,
  ButtonsWrapper,
  GuestPanelContainer,
  ShareRegistrationLinkButton,
} from '../styled';

const PanelGroupTitle = ({
  children,
  className,
  isNew,
}: PropsWithChildren & {className?: string; isNew?: boolean}) => {
  return (
    <div
      className={cn(
        'text-sm font-semibold leading-6 mb-2.5 flex gap-2 items-center text-[var(--font-secondary)] uppercase',
        'panel-group-title',
        'kiosko:text-kiosko-s',
        isNew && 'w-full mb-0',
        className,
      )}
    >
      {children}
    </div>
  );
};

type Props = {
  handleRegisterNewGuest?: (isLeader?: boolean) => void;
  nextPage: string;
};
function GuestsList({handleRegisterNewGuest, nextPage}: Props) {
  const {guestGroup, isLoading, isFetching} = useGuestGroup();
  const hasNoGuests = guestGroup?.added_number_of_guests === 0;
  const theme = useTheme();
  const {t} = useTranslation();
  const {checkCanShowRegisterButton} = useReservationSettings();
  const isRegisterGuestButtonVisible = checkCanShowRegisterButton();
  const members = guestGroup?.members;
  const leaderGuest = members?.find(member => member.id === guestGroup?.leader_id);
  const accompanyingMembers =
    members?.filter(member => member.id !== guestGroup?.leader_id) ?? [];
  const knownNumberOfGuests = guestGroup?.known_number_of_guests ?? 0;

  const showAccompanyingGuests = knownNumberOfGuests > 1;
  const isKiosko = useLayoutMode() === 'kiosko';

  if (isLoading) {
    return (
      <LoaderWrapper className="kiosko:w-full">
        <MainLoader visible width={80} />
      </LoaderWrapper>
    );
  }

  return isKiosko ? (
    <>
      {hasNoGuests ? (
        <>
          <StyledIcon
            name={'iconRegister'}
            color={theme.colors.branding.primary}
            custom
            size={200}
            className="guest-register-view__icon-register"
          />
          <Title className="guest-register-view__no-guests-title">
            {t('no_registered_guest')}
          </Title>
          <SubTitle className="guest-register-view__no-guests-subtitle">
            {t('click_register_guest')}
          </SubTitle>
          <ButtonsWrapper className="guest-register-view__btns">
            <Button loading={isFetching} label={t('register_guest')} to={nextPage} />
            {ChekinProSettings.canShareRegistrationLink && (
              <ShareRegistrationLinkButton />
            )}
          </ButtonsWrapper>
        </>
      ) : (
        <>
          <PanelGroupTitle className="guest-register-view__group-title">
            {t('guest_in_charge')}
            <Crown
              size="20"
              color="#FFD166"
              fill="#FFD166"
              className="top-[-1px] relative"
            />
          </PanelGroupTitle>
          <GuestPanelContainer
            className={cn('guest-register-view__guests-panel', 'kiosko:w-full')}
          >
            {leaderGuest && (
              <GuestItem
                isLoading={isFetching}
                guestMember={{...leaderGuest, isLeader: true}}
              />
            )}
          </GuestPanelContainer>
          {showAccompanyingGuests &&
            accompanyingMembers &&
            accompanyingMembers.length > 0 && (
              <>
                <PanelGroupTitle className="guest-register-view__group-title">
                  {t('accompanying_guests')}
                </PanelGroupTitle>
                <GuestPanelContainer
                  className={cn('guest-register-view__guests-panel', 'kiosko:w-full')}
                >
                  {accompanyingMembers.map((member, index) => (
                    <GuestItem isLoading={isFetching} guestMember={member} key={index} />
                  ))}
                </GuestPanelContainer>
              </>
            )}
          {isRegisterGuestButtonVisible && (
            <RegisterButton
              to={nextPage}
              loading={isFetching}
              onClick={() => handleRegisterNewGuest?.(members?.length === 0)}
              label={t('register_another_guest')}
              className={cn(
                'guest-register-view__register-btn',
                'kiosko:w-[698px] kiosko:max-w-full kiosko:h-[114px] kiosko:rounded-[56px] kiosko:mt-[24px]',
                'kiosko:[&_.buttonLabel]:text-kiosko-m kiosko:[&_.buttonLabel]:leading-normal',
              )}
            />
          )}
          {ChekinProSettings.canShareRegistrationLink && (
            <ShareRegistrationLinkButton
              className={cn('guest-register-view__share-btn', 'kiosko:mt-0')}
            />
          )}
        </>
      )}
    </>
  ) : (
    <GuestListNew
      accompanyingMembers={accompanyingMembers}
      knownNumberOfGuests={knownNumberOfGuests}
      showAccompanyingGuests={showAccompanyingGuests}
      leaderGuest={leaderGuest}
      isFetching={isFetching}
      onRegisterNew={() => handleRegisterNewGuest?.(members?.length === 0)}
    />
  );
}

const EmptyGuest = {
  id: '',
  document_number: '',
  is_pre_registered: false,
  email: '',
  participant_id: '',
  full_name: '',
  guestapp_status: GUEST_STATUSES.incomplete,
  isLeader: false,
};

function GuestListNew({
  accompanyingMembers,
  knownNumberOfGuests,
  showAccompanyingGuests,
  leaderGuest,
  isFetching,
  onRegisterNew,
}: {
  accompanyingMembers: ShortGuest[];
  knownNumberOfGuests: number;
  showAccompanyingGuests: boolean;
  leaderGuest?: ShortGuest;
  isFetching: boolean;
  onRegisterNew: () => void;
}) {
  const {t} = useTranslation();

  const withEmptyAccompanyingMembers = Array.from(
    {length: Math.max(accompanyingMembers.length, knownNumberOfGuests - 1)},
    (_, index) => {
      return accompanyingMembers[index] ?? EmptyGuest;
    },
  );

  return (
    <div className="w-full space-y-4">
      <PanelGroupTitle className="guest-register-view__group-title" isNew>
        {t('guest_in_charge')}
        <Crown size="20" color="#FFD166" fill="#FFD166" className="top-[-1px] relative" />
      </PanelGroupTitle>

      <div className="w-full grid xl:grid-cols-2 gap-6">
        <GuestItem
          isLoading={isFetching}
          guestMember={leaderGuest ?? {...EmptyGuest, isLeader: true}}
          isNew
          onRegisterNew={onRegisterNew}
        />
      </div>

      {showAccompanyingGuests && (
        <>
          <PanelGroupTitle className="guest-register-view__group-title" isNew>
            {t('accompanying_guests')}
          </PanelGroupTitle>

          <div className="w-full grid xl:grid-cols-2 gap-6">
            {withEmptyAccompanyingMembers.map((member, index) => (
              <GuestItem
                isLoading={isFetching}
                guestMember={{...member, isLeader: false}}
                key={index}
                isNew
                onRegisterNew={onRegisterNew}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export {GuestsList};
